import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Info, Search, ChevronUp, ChevronDown, Archive, Pencil, ExternalLink, CheckSquare } from 'lucide-react';
import { useLeads } from '../../context/LeadsContext';
import { useSettings } from '../../context/SettingsContext';
import { formatDate, formatAddress } from '../../utils/helpers';
import { getDisplayName } from '../../lib/utils';
import { matchesQuery } from '../../utils/contactSearch';
import LeadInfoPopover from '../pipeline/LeadInfoPopover';
import NewLeadModal from './NewLeadModal';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { useNavigate } from 'react-router-dom';
import CardStatusBadge from '../CardStatusBadge';

const LeadsTable = () => {
  const navigate = useNavigate();
  const { leads, updateLead } = useLeads();
  const { pipelineStages, ARCHIVED_STAGE } = useSettings();
  const [searchTerm, setSearchTerm] = useState('');
  const [stageFilter, setStageFilter] = useState('active'); // 'active' | 'archived'
  const [sortField, setSortField] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedLead, setSelectedLead] = useState(null);
  const [editingLead, setEditingLead] = useState(null);
  const itemsPerPage = 20;

  const scrollContainerRef = useRef(null);
  const ghostScrollRef = useRef(null);

  // Sync horizontal scroll between top ghost bar and table container
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    const ghostScroll = ghostScrollRef.current;

    if (!scrollContainer || !ghostScroll) return;

    const innerGhost = ghostScroll.firstChild;
    if (innerGhost) {
      innerGhost.style.width = `${scrollContainer.scrollWidth}px`;
    }

    const handleContainerScroll = () => {
      ghostScroll.scrollLeft = scrollContainer.scrollLeft;
    };

    const handleGhostScroll = () => {
      scrollContainer.scrollLeft = ghostScroll.scrollLeft;
    };

    scrollContainer.addEventListener('scroll', handleContainerScroll);
    ghostScroll.addEventListener('scroll', handleGhostScroll);

    const handleResize = () => {
      if (innerGhost) {
        innerGhost.style.width = `${scrollContainer.scrollWidth}px`;
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      scrollContainer.removeEventListener('scroll', handleContainerScroll);
      ghostScroll.removeEventListener('scroll', handleGhostScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Recompute ghost width when leads change (columns/content may change width)
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    const ghostScroll = ghostScrollRef.current;
    if (!scrollContainer || !ghostScroll) return;

    const innerGhost = ghostScroll.firstChild;
    if (innerGhost) {
      innerGhost.style.width = `${scrollContainer.scrollWidth}px`;
    }
  }, [leads]);

  // Filter leads with Archived behavior
  const filteredLeads = useMemo(() => {
    const search = (searchTerm || '').toLowerCase();
    const isArchivedSearch = search === 'archived';

    return leads.filter((lead) => {
      const isArchived = lead.stage === ARCHIVED_STAGE;

      // Stage filter logic
      if (stageFilter === 'archived') {
        if (!isArchived) return false;
      } else {
        // active filter
        if (isArchived && !isArchivedSearch) {
          // hide archived unless user explicitly searches for "archived"
          return false;
        }
      }

      if (!search) return true;

      // Use shared contact-search helper so phone numbers match regardless of
      // formatting (e.g. "2039096881", "203-909-6881", "(203) 909-6881" all
      // match). Preserves existing name/email/address/service text matching.
      return matchesQuery(lead, searchTerm, {
        resolvedName: getDisplayName(lead, ''),
        extraTextFields: ['service'],
      });
    });
  }, [leads, stageFilter, ARCHIVED_STAGE, searchTerm]);

  // Sort leads
  const sortedLeads = useMemo(() => {
    const sorted = [...filteredLeads];
    
    sorted.sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      // Handle timestamps
      if (sortField === 'createdAt' || sortField === 'lastContact') {
        aVal = aVal?.toDate?.() || aVal || new Date(0);
        bVal = bVal?.toDate?.() || bVal || new Date(0);
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      }

      // Handle address sorting using nested address object
      if (sortField === 'address') {
        const getAddressString = (lead) => {
          if (lead.address && typeof lead.address === 'object') {
            const parts = [];
            if (lead.address.streetNumber) parts.push(String(lead.address.streetNumber));
            if (lead.address.streetName) parts.push(String(lead.address.streetName));
            if (lead.address.city) parts.push(String(lead.address.city));
            if (lead.address.state) parts.push(String(lead.address.state));
            if (lead.address.zip) parts.push(String(lead.address.zip));
            return parts.join(' ').toLowerCase();
          }
          if (typeof lead.address === 'string') {
            return lead.address.toLowerCase();
          }
          // No root-level address support anymore; fallback to empty string
          return '';
        };

        const aAddress = getAddressString(a);
        const bAddress = getAddressString(b);

        if (sortDirection === 'asc') {
          return aAddress > bAddress ? 1 : -1;
        } else {
          return aAddress < bAddress ? 1 : -1;
        }
      }

      // Handle strings
      aVal = String(aVal || '').toLowerCase();
      bVal = String(bVal || '').toLowerCase();

      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

    return sorted;
  }, [filteredLeads, sortField, sortDirection]);

  // Paginate
  const paginatedLeads = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return sortedLeads.slice(startIndex, startIndex + itemsPerPage);
  }, [sortedLeads, currentPage]);

  const totalPages = Math.ceil(sortedLeads.length / itemsPerPage);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const handleStageChange = async (leadId, newStage) => {
    try {
      await updateLead(leadId, { stage: newStage });
    } catch (error) {
      console.error('Error updating stage:', error);
    }
  };

  const handleArchive = async (leadId) => {
    try {
      await updateLead(leadId, { stage: ARCHIVED_STAGE });
    } catch (error) {
      console.error('Error archiving lead:', error);
    }
  };

  const handleRowClick = (leadId) => {
    navigate(`/leads/${leadId}`);
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ChevronUp className="h-4 w-4 text-gray-300" />;
    return sortDirection === 'asc' ? 
      <ChevronUp className="h-4 w-4 text-[var(--cb-orange)]" /> : 
      <ChevronDown className="h-4 w-4 text-[var(--cb-orange)]" />;
  };

  return (
    <div className="space-y-3 md:space-y-4">
      {/* Search Bar - Responsive */}
      <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            type="text"
            placeholder="Search leads..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            className="pl-10 h-11 lg:h-10"
            data-testid="leads-search-input"
          />
        </div>
        <div className="flex gap-2 items-center">
          {/* Stage Filter */}
          <div className="w-32 sm:w-40">
            <Select
              value={stageFilter}
              onValueChange={(value) => {
                setStageFilter(value);
                setCurrentPage(1);
              }}
            >
              <SelectTrigger className="h-10 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active" className="text-xs">
                  All Active
                </SelectItem>
                <SelectItem value="archived" className="text-xs">
                  Archived
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="text-sm text-gray-500 hidden sm:block">
            {sortedLeads.length} lead{sortedLeads.length !== 1 ? 's' : ''}
          </div>
        </div>
      </div>

      {/* ── Mobile card list (< lg) — fast scrollable intake list ──────────── */}
      <div className="lg:hidden -mx-4" data-testid="leads-mobile-list">
        {paginatedLeads.length === 0 ? (
          <div className="text-center py-10 text-gray-400 text-sm px-4">
            {searchTerm ? 'No leads match your search' : 'No leads yet'}
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {paginatedLeads.map((lead) => {
              const address = formatAddress(lead.address);
              const dateLabel = formatDate(lead.createdAt);
              const stage = lead.stage || 'New';

              return (
                <div
                  key={lead.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => handleRowClick(lead.id)}
                  onKeyDown={(e) => e.key === 'Enter' && handleRowClick(lead.id)}
                  className="flex items-center gap-3 px-4 py-3.5 hover:bg-gray-50 active:bg-gray-100 active:scale-[0.99] transition-all cursor-pointer min-h-[72px]"
                  data-testid={`lead-card-${lead.id}`}
                >
                  {/* Orange left-accent line */}
                  <div className="w-0.5 h-10 rounded-full bg-[var(--cb-orange)] shrink-0 opacity-60" />

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    {/* Row 1: name */}
                    <div className="font-bold text-[15px] text-gray-900 truncate leading-snug">
                      {getDisplayName(lead)}
                    </div>
                    {/* Row 2: address */}
                    {address && address !== '-' && (
                      <div className="text-sm text-gray-500 truncate mt-0.5">
                        {address}
                      </div>
                    )}
                    {/* Row 3: stage dot · date */}
                    <div className="flex items-center gap-1.5 mt-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-[var(--cb-orange)] shrink-0" />
                      <span className="text-xs text-gray-500 truncate">{stage}</span>
                      {dateLabel && (
                        <>
                          <span className="text-gray-300 text-xs">·</span>
                          <span className="text-xs text-gray-400">{dateLabel}</span>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Chevron */}
                  <svg className="h-4 w-4 text-gray-300 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              );
            })}
          </div>
        )}

        {/* Mobile count */}
        <div className="text-xs text-gray-400 text-center py-3 px-4">
          {sortedLeads.length} lead{sortedLeads.length !== 1 ? 's' : ''}
        </div>
      </div>

      {/* ── Desktop table (≥ lg) ─────────────────────────────────────────────── */}
      <div className="hidden lg:block">
        {/* Top horizontal scrollbar */}
        <div
          ref={ghostScrollRef}
          className="overflow-x-auto mb-2"
          style={{ height: '12px' }}
        >
          <div className="h-px" />
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
          <div ref={scrollContainerRef} className="overflow-x-auto">
            <table className="w-full min-w-[900px]">
              <thead className="bg-gray-50 border-b border-gray-200 sticky top-0 z-10">
              <tr>
                <th
                  className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('name')}
                >
                  <div className="flex items-center gap-1">
                    Name
                    <SortIcon field="name" />
                  </div>
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('address')}
                >
                  <div className="flex items-center gap-1">
                    Address
                    <SortIcon field="address" />
                  </div>
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Service
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Stage
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('createdAt')}
                >
                  <div className="flex items-center gap-1">
                    Created
                    <SortIcon field="createdAt" />
                  </div>
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('lastContact')}
                >
                  <div className="flex items-center gap-1">
                    Last Contact
                    <SortIcon field="lastContact" />
                  </div>
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Card
                </th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {paginatedLeads.map((lead) => (
                <tr
                  key={lead.id}
                  className="hover:bg-gray-50 transition-colors relative"
                >
                  <td className="px-4 py-3 text-sm" style={{ color: 'var(--cb-navy)' }}>
                    <button
                      type="button"
                      onClick={() => handleRowClick(lead.id)}
                      className="font-medium text-left w-full hover:underline"
                    >
                      {getDisplayName(lead)}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {formatAddress(lead.address)}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {lead.service && (
                      <span className="inline-block px-2 py-1 text-xs rounded" style={{
                        backgroundColor: 'rgba(242, 107, 29, 0.1)',
                        color: 'var(--cb-orange)'
                      }}>
                        {lead.service}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <Select
                      value={lead.stage}
                      onValueChange={(value) => handleStageChange(lead.id, value)}
                    >
                      <SelectTrigger className="w-32 h-8 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {pipelineStages.map((stage) => (
                          <SelectItem key={stage} value={stage} className="text-xs">
                            {stage}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {formatDate(lead.createdAt)}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {formatDate(lead.lastContact)}
                  </td>
                  <td className="px-4 py-3" data-testid={`lead-card-col-${lead.id}`}>
                    <CardStatusBadge
                      cardOnFileStatus={lead.cardOnFileStatus}
                      cardBrand={lead.cardBrand}
                      cardLast4={lead.cardLast4}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/leads/${lead.id}`);
                        }}
                        className="p-1 hover:bg-gray-100 rounded transition-colors"
                        title="Open lead profile"
                      >
                        <ExternalLink className="h-4 w-4 text-gray-400 hover:text-[var(--cb-orange)]" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedLead(selectedLead?.id === lead.id ? null : lead);
                        }}
                        className="p-1 hover:bg-gray-100 rounded transition-colors"
                        title="Open tasks"
                      >
                        <CheckSquare className="h-4 w-4 text-gray-400 hover:text-[var(--cb-orange)]" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingLead(lead);
                        }}
                        className="p-1 hover:bg-gray-100 rounded transition-colors"
                        title="Edit"
                      >
                        <Pencil className="h-4 w-4 text-gray-400 hover:text-[var(--cb-orange)]" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleArchive(lead.id);
                        }}
                        className="p-1 hover:bg-red-50 rounded transition-colors"
                        title="Archive"
                      >
                        <Archive className="h-4 w-4 text-gray-400 hover:text-red-500" />
                      </button>
                    </div>

                    {/* Info Popover */}
                    {selectedLead?.id === lead.id && (
                      <div className="fixed right-4 top-24 z-40">
                        <LeadInfoPopover
                          lead={lead}
                          onClose={() => setSelectedLead(null)}
                          position="left"
                          variant="inline"
                        />
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Empty State */}
          {paginatedLeads.length === 0 && (
            <div className="text-center py-12 text-gray-400">
              {searchTerm ? 'No leads found matching your search' : 'No leads yet'}
            </div>
          )}
        </div>
      </div>
      </div> {/* end desktop table */}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-2">
          <div className="text-sm text-gray-500 hidden sm:block">
            Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, sortedLeads.length)} of {sortedLeads.length}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="h-10 px-3"
            >
              Previous
            </Button>
            <div className="hidden sm:flex items-center gap-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }

                return (
                  <Button
                    key={pageNum}
                    variant={currentPage === pageNum ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setCurrentPage(pageNum)}
                    className={currentPage === pageNum ? 'bg-[var(--cb-orange)] hover:bg-[var(--cb-orange)]/90' : ''}
                  >
                    {pageNum}
                  </Button>
                );
              })}
            </div>
            {/* Mobile: Show current page */}
            <div className="sm:hidden flex items-center px-3 text-sm text-gray-600">
              {currentPage} / {totalPages}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="h-10 px-3"
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Edit Lead Modal (shared create/edit component) */}
      <NewLeadModal
        isOpen={!!editingLead}
        onClose={() => setEditingLead(null)}
        mode="edit"
        lead={editingLead}
      />
    </div>
  );
};

export default LeadsTable;
